import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, FileText, Target } from "lucide-react";
import AIScoreBadge, { tierConfig } from "./entity-detail/AIScoreBadge";

const STAGE_BADGE = {
  prospect: { label: "Prospect", color: "#64748b" },
  contact: { label: "Prise de contact", color: "#3b82f6" },
  qualification: { label: "Qualification", color: "#8b5cf6" },
  devis: { label: "Devis envoyé", color: "#f59e0b" },
  negociation: { label: "Négociation", color: "#f97316" },
  gagne: { label: "Gagné", color: "#22c55e" },
  perdu: { label: "Perdu", color: "#ef4444" },
};

const eur = (n) =>
  Number(n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

/**
 * Liste des opportunités CRM liées à un client + leur score IA.
 * Cliquable → page dédiée /admin/opportunite/:id avec scoring IA complet.
 */
export default function ClientCrmOpportunities({ api, clientId, testId = "client-crm-opps" }) {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    const load = async () => {
      try {
        const r = await api.get(`/crm/opportunities?client_id=${clientId}`);
        setOpps(r.data || []);
      } catch {
        setOpps([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, clientId]);

  // Aggregate AI summary: best score wins
  const bestScored = opps
    .filter((o) => o.ai_evaluation?.score >= 0)
    .sort((a, b) => (b.ai_evaluation?.score || 0) - (a.ai_evaluation?.score || 0))[0];

  const totalAmount = opps.reduce((s, o) => s + Number(o.amount || 0), 0);
  const wonAmount = opps.filter((o) => o.stage === "gagne").reduce((s, o) => s + Number(o.amount || 0), 0);

  if (loading) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5" data-testid={`${testId}-loading`}>
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement des opportunités…
        </div>
      </div>
    );
  }

  if (!opps.length) {
    return (
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-8 text-center" data-testid={`${testId}-empty`}>
        <FileText className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-400">Aucune opportunité CRM liée à ce client.</p>
        <p className="text-xs text-slate-500 mt-1">Créez-en une depuis le module CRM.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={testId}>
      {/* Top KPI summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total</div>
          <div className="text-lg font-bold text-white mt-1">{opps.length}</div>
          <div className="text-[10px] text-slate-500">opportunités</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Montant pipeline</div>
          <div className="text-lg font-bold text-[#f7c935] mt-1">{eur(totalAmount)}</div>
          <div className="text-[10px] text-slate-500">cumulé</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Gagné</div>
          <div className="text-lg font-bold text-emerald-400 mt-1">{eur(wonAmount)}</div>
          <div className="text-[10px] text-slate-500">{opps.filter((o) => o.stage === "gagne").length} affaires</div>
        </div>
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-3.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Meilleur score IA</div>
          {bestScored ? (
            <div className="mt-1">
              <AIScoreBadge tier={bestScored.ai_evaluation.tier} score={bestScored.ai_evaluation.score} size="md" />
              <div className="text-[10px] text-slate-500 mt-1 truncate">{bestScored.title}</div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 mt-1">— Non évalué</div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {opps.map((o) => {
          const sb = STAGE_BADGE[o.stage] || { label: o.stage || "—", color: "#64748b" };
          const ai = o.ai_evaluation;
          return (
            <Link
              key={o.opportunity_id}
              to={`/admin/opportunite/${o.opportunity_id}`}
              className="block bg-[#14101c] hover:bg-[#1a1525] border border-white/5 hover:border-white/15 rounded-xl p-4 transition-all hover:-translate-y-0.5 group"
              data-testid={`opp-card-${o.opportunity_id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-white truncate">{o.title}</span>
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: sb.color + "22", color: sb.color }}
                    >
                      {sb.label}
                    </span>
                    {ai && <AIScoreBadge tier={ai.tier} score={ai.score} size="sm" />}
                  </div>
                  {o.contact_name && <div className="text-xs text-slate-500 mt-1">Contact : {o.contact_name}</div>}
                  {ai?.headline && (
                    <div className="text-xs text-slate-300 mt-2 italic flex items-start gap-1.5">
                      <Target className="w-3 h-3 mt-0.5 text-amber-400 flex-shrink-0" />
                      <span className="line-clamp-2">{ai.headline}</span>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-[#f7c935]">{eur(o.amount)}</div>
                  <div className="text-[10px] text-slate-500">{o.probability}%</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-white transition-colors flex-shrink-0 self-center" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
